/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { ArrowRight, ShieldCheck, Star, Home, Sparkles, TrendingUp, Users, CheckCircle2, Building2, User } from 'lucide-react';
import { QuickLeadForm } from './QuickLeadForm';
import { PersonaModal } from './PersonaModal';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: (i = 0) => ({
        opacity: 1, y: 0,
        transition: { duration: 0.6, delay: i * 0.08, ease: [0.21, 0.5, 0.26, 1] },
    }),
};

/**
 * HeroPreview — Mini "tableau de bord" illustratif qui remplace les paragraphes
 * de texte par de la matière visible. Trois cartes flottantes décalées :
 *   1. Mandat suivi en temps réel (vivacité du cockpit)
 *   2. Avis vendeur 5★ (preuve qualité → réputation)
 *   3. Indicateur d'équipe (pilotage collectif)
 *
 * Effet « Linear/Vercel » : pas de mockup screenshoté, juste des données
 * crédibles et stylisées avec une animation flottante très douce.
 */
const HeroPreview = () => (
    <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.85, delay: 0.4, ease: [0.21, 0.5, 0.26, 1] }}
        className="relative max-w-3xl mx-auto mt-14 mb-2"
        data-testid="hero-preview"
        aria-hidden="true"
    >
        {/* Ambient glow */}
        <div className="pointer-events-none absolute -inset-12 bg-gradient-to-br from-[#681793]/15 via-transparent to-[#FF914D]/10 blur-3xl" />

        <div className="relative grid grid-cols-3 gap-3 md:gap-5">
            {/* Carte 1 — Mandat suivi */}
            <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0 }}
                className="col-span-2 sm:col-span-2 rounded-2xl border border-white/10 bg-[#0B0913]/80 backdrop-blur-xl p-4 md:p-5 shadow-2xl shadow-black/50"
            >
                <div className="flex items-center gap-2 mb-3">
                    <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                    </span>
                    <span className="text-[10px] tracking-widest text-emerald-300/80 uppercase" style={MONO}>
                        Mandat suivi · J+14
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#A855F7]/15 flex items-center justify-center flex-shrink-0">
                        <Home className="w-5 h-5 text-[#C7A1F0]" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-white truncate" style={DISPLAY}>
                            Appartement · Lyon 6e
                        </p>
                        <p className="text-xs text-[#A19DAB] truncate">
                            M. & Mme Dupont · agent : Léa M.
                        </p>
                    </div>
                </div>
                {/* Mini progress */}
                <div className="mt-4">
                    <div className="flex justify-between text-[10px] text-[#A19DAB] mb-1.5">
                        <span style={MONO}>QUALITÉ DU SUIVI</span>
                        <span className="text-white font-semibold">92%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: '92%' }}
                            transition={{ duration: 1.4, delay: 1.0, ease: 'easeOut' }}
                            className="h-full bg-gradient-to-r from-[#A855F7] to-[#FF914D]"
                        />
                    </div>
                </div>
            </motion.div>

            {/* Carte 2 — Avis vendeur */}
            <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
                className="col-span-1 sm:col-span-1 rounded-2xl border border-white/10 bg-[#0B0913]/80 backdrop-blur-xl p-4 shadow-2xl shadow-black/50 flex flex-col"
            >
                <div className="flex items-center gap-1 mb-2">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <Star key={i} className="w-3.5 h-3.5 text-[#FF914D] fill-[#FF914D]" />
                    ))}
                </div>
                <p className="text-xs md:text-sm text-white/90 leading-snug italic line-clamp-3" style={DISPLAY}>
                    « Léa nous a accompagnés avec une rigueur exemplaire. »
                </p>
                <p className="text-[10px] text-[#6E6A7A] mt-auto pt-3" style={MONO}>
                    AVIS VENDEUR · VÉRIFIÉ
                </p>
            </motion.div>

            {/* Carte 3 — Indicateur équipe (visible sm+) */}
            <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
                className="hidden sm:flex col-span-3 sm:col-span-2 lg:col-span-2 rounded-2xl border border-white/10 bg-[#0B0913]/80 backdrop-blur-xl p-4 md:p-5 shadow-2xl shadow-black/50 items-center gap-4"
            >
                <div className="w-11 h-11 rounded-xl bg-[#FF914D]/15 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#FF914D]" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#A19DAB] truncate" style={MONO}>
                        ÉQUIPE · CETTE SEMAINE
                    </p>
                    <p className="text-sm font-semibold text-white mt-0.5 truncate" style={DISPLAY}>
                        6 mandats actifs · 4 avis recueillis
                    </p>
                </div>
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/20 flex-shrink-0">
                    <TrendingUp className="w-3 h-3 text-emerald-300" />
                    <span className="text-[10px] font-semibold text-emerald-300" style={MONO}>+18%</span>
                </div>
            </motion.div>

            {/* Carte 4 — Mini badge réputation (visible sm+) */}
            <motion.div
                animate={{ y: [0, -7, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1.8 }}
                className="hidden sm:flex col-span-1 rounded-2xl border border-[#A855F7]/25 bg-gradient-to-br from-[#A855F7]/15 via-[#0B0913]/80 to-[#0B0913]/80 backdrop-blur-xl p-4 shadow-2xl shadow-black/50 flex-col items-start justify-center"
            >
                <CheckCircle2 className="w-5 h-5 text-[#C7A1F0] mb-2" />
                <p className="text-[10px] text-[#A19DAB] uppercase tracking-widest" style={MONO}>
                    Page agence
                </p>
                <p className="text-sm font-bold text-white mt-1" style={DISPLAY}>
                    Vérifiée
                </p>
            </motion.div>
        </div>
    </motion.div>
);

/**
 * ConvHero — Iter168 « Punch + Visuel ».
 *
 * Direction : raccourcir radicalement le H1 (compréhension < 1s),
 * réduire le sous-titre à une phrase de support, et remplacer le
 * bloc texte dense par un mini tableau de bord vivant qui rend le
 * produit immédiatement tangible.
 */
export const ConvHero = ({ onDemoClick }) => {
    const [personaModal, setPersonaModal] = React.useState(null); // null | 'agences' | 'agents'

    return (
        <section className="relative overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24" data-testid="hero-section">
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/2 top-24 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#681793]/25 blur-[140px]" />
                <div className="absolute left-1/2 top-72 -translate-x-1/3 w-[500px] h-[500px] rounded-full bg-[#FF914D]/15 blur-[150px]" />
            </div>

            <div className="relative max-w-6xl mx-auto px-6 md:px-12 text-center">
                <motion.div initial="hidden" animate="visible" custom={0} variants={fadeUp}>
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-[11px] font-medium tracking-widest text-[#A19DAB] uppercase"
                          style={MONO}>
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF914D] animate-pulse" />
                        Cockpit · Réseau · Réputation
                    </span>
                </motion.div>

                <motion.h1
                    initial="hidden" animate="visible" custom={1} variants={fadeUp}
                    className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white max-w-4xl mx-auto leading-[1]"
                    style={DISPLAY}
                    data-testid="hero-title"
                >
                    Vos mandats sous contrôle.
                    <br />
                    <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                        Votre réputation qui grandit.
                    </span>
                </motion.h1>

                <motion.p
                    initial="hidden" animate="visible" custom={2} variants={fadeUp}
                    className="mt-5 text-base md:text-lg text-[#A19DAB] max-w-2xl mx-auto leading-relaxed"
                    data-testid="hero-subtitle"
                >
                    Le cockpit qui transforme chaque accompagnement vendeur en preuve
                    de qualité pour votre agence.
                </motion.p>

                <motion.div
                    initial="hidden" animate="visible" custom={3} variants={fadeUp}
                    className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <Button
                        onClick={onDemoClick}
                        data-testid="hero-demo-request-button"
                        className="group bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-95 text-white font-semibold text-base px-8 h-13 py-6 rounded-full shadow-xl shadow-[#681793]/40 transition-all hover:scale-[1.02]"
                    >
                        Demander une démonstration
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                    </Button>
                    <a
                        href="/pour-les-agences"
                        data-testid="hero-pour-agences-link"
                        className="group inline-flex items-center gap-2 text-white/90 hover:text-white font-medium text-base px-6 h-13 py-6 rounded-full border border-white/15 hover:border-white/30 transition-all"
                    >
                        <Sparkles className="w-4 h-4 text-[#FF914D]" />
                        Voir comment ça marche
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </a>
                </motion.div>

                <motion.div
                    initial="hidden" animate="visible" custom={4} variants={fadeUp}
                    className="mt-5 flex items-center justify-center gap-2 text-xs md:text-sm text-[#6E6A7A]"
                >
                    <ShieldCheck className="w-4 h-4 text-[#A855F7]" />
                    Sans engagement · Démo personnalisée · Réponse sous 24h
                </motion.div>

                {/* Mini formulaire de capture lead — accélère la conversion */}
                <motion.div
                    initial="hidden" animate="visible" custom={5} variants={fadeUp}
                    className="mt-10"
                    data-testid="hero-quick-lead-form-wrap"
                >
                    <QuickLeadForm
                        variant="hero"
                        label="Vous préférez qu'on vous rappelle ? Laissez-nous vos coordonnées."
                        ctaLabel="Être recontacté"
                        source="hero_quick_form"
                        data-testid="hero-quick-lead-form"
                    />
                </motion.div>

                {/* Visuel produit : mini tableau de bord vivant */}
                <HeroPreview />

                {/* Sélecteur de parcours — boutons pill très incitatifs */}
                <motion.div
                    initial="hidden" animate="visible" custom={6} variants={fadeUp}
                    className="mt-12 flex flex-col items-center justify-center gap-3"
                    data-testid="hero-persona-selector"
                >
                    <p className="text-[11px] uppercase tracking-widest text-[#A19DAB]" style={MONO}>
                        Voir le pitch dédié <span className="text-[#FF914D]">en 30 secondes</span> ↓
                    </p>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <motion.button
                            type="button"
                            onClick={() => setPersonaModal('agences')}
                            data-testid="hero-link-pour-agences"
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.98 }}
                            className="group relative overflow-hidden inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border-2 border-[#FF914D]/50 bg-gradient-to-br from-[#FF914D]/15 to-transparent hover:border-[#FF914D] text-sm font-semibold text-white transition-colors shadow-lg shadow-[#FF914D]/15 hover:shadow-[#FF914D]/30"
                        >
                            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent group-hover:translate-x-full transition-transform duration-1000 ease-out" aria-hidden="true" />
                            <span className="relative flex w-2 h-2">
                                <span className="absolute inline-flex w-full h-full rounded-full bg-[#FF914D] opacity-80 animate-ping" />
                                <span className="relative inline-flex w-2 h-2 rounded-full bg-[#FF914D]" />
                            </span>
                            <Building2 className="relative w-4 h-4 text-[#FF914D]" />
                            <span className="relative">Je dirige une agence</span>
                            <ArrowRight className="relative w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </motion.button>

                        <motion.button
                            type="button"
                            onClick={() => setPersonaModal('agents')}
                            data-testid="hero-link-pour-agents"
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.98 }}
                            className="group relative overflow-hidden inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border-2 border-[#A855F7]/50 bg-gradient-to-br from-[#A855F7]/15 to-transparent hover:border-[#A855F7] text-sm font-semibold text-white transition-colors shadow-lg shadow-[#A855F7]/15 hover:shadow-[#A855F7]/30"
                        >
                            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent group-hover:translate-x-full transition-transform duration-1000 ease-out" aria-hidden="true" />
                            <span className="relative flex w-2 h-2">
                                <span className="absolute inline-flex w-full h-full rounded-full bg-[#A855F7] opacity-80 animate-ping" style={{ animationDelay: '0.6s' }} />
                                <span className="relative inline-flex w-2 h-2 rounded-full bg-[#A855F7]" />
                            </span>
                            <User className="relative w-4 h-4 text-[#C7A1F0]" />
                            <span className="relative">Je suis agent immobilier</span>
                            <ArrowRight className="relative w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </motion.button>
                    </div>
                    <p className="text-[10px] text-[#6E6A7A] mt-1" style={MONO}>
                        Aperçu rapide · Vous restez sur la page
                    </p>
                    {/* Sub-CTA vendeur — discret, ne pollue pas le funnel pro */}
                    <p className="mt-5 text-xs text-[#6E6A7A]" data-testid="hero-seller-subcta">
                        Vous êtes vendeur ?{' '}
                        <a
                            href="/recherche"
                            className="text-[#C7A1F0] hover:text-white underline underline-offset-2 decoration-dotted transition-colors"
                            data-testid="hero-seller-search-link"
                        >
                            Trouvez un agent de confiance →
                        </a>
                    </p>
                </motion.div>
            </div>

            {/* Persona modal — affiche pitch sans quitter la landing */}
            <PersonaModal
                persona={personaModal}
                isOpen={personaModal !== null}
                onClose={() => setPersonaModal(null)}
                onDemoClick={onDemoClick}
            />
        </section>
    );
};
